// CD: components/search/SearchBar.jsx
import { defineOnce, routesRoot, fetchSectionDocument, emit, showToast } from './pushe-ui.js';

export const DEBOUNCE_MS = 250;
const REQUEST_TIMEOUT_MS = 10000;
export function buildSuggestUrl(root, q, { limit = 5 } = {}) {
  const params = new URLSearchParams({ q, 'resources[type]': 'product,collection,page', 'resources[limit]': limit, section_id: 'pushe-predictive-search' });
  return `${String(root).replace(/\/$/, '')}/search/suggest?${params}`;
}
export const shouldQuery = q => [...String(q ?? '').trim()].length >= 2;
export function debounce(callback, wait = DEBOUNCE_MS) {
  let timer;
  const call = (...args) => { clearTimeout(timer); timer = setTimeout(() => { timer = undefined; callback(...args); }, wait); };
  call.cancel = () => { clearTimeout(timer); timer = undefined; };
  call.pending = () => timer !== undefined;
  return call;
}

export class PushePredictiveSearch extends HTMLElement {
  #off = [];
  #request;
  #revision = 0;
  #form;
  #input;
  #schedule = debounce(query => this.#query(query));

  connectedCallback() {
    if (this.#off.length) return;
    this.#form = this.querySelector('form.pushe-search');
    this.#input = this.#form?.querySelector('.pushe-search__input');
    if (!this.#input) return;
    const listen = (target, type, fn) => { target.addEventListener(type, fn); this.#off.push(() => target.removeEventListener(type, fn)); };
    listen(this.#input, 'input', () => this.#changed());
    listen(this.#input, 'focusin', () => {
      const panel = this.#panel();
      if (shouldQuery(this.#input.value) && !panel?.childNodes.length) this.#schedule(this.#input.value.trim());
    });
    listen(this.#form, 'click', event => {
      if (!event.target.closest('.pushe-search__clear')) return;
      event.preventDefault();
      this.#input.value = '';
      this.#changed();
      this.#input.focus();
    });
    listen(this.#form, 'keydown', event => this.#key(event));
    listen(this.#form, 'focusout', event => {
      if (!this.#form.contains(event.relatedTarget)) this.#close();
    });
    listen(this.ownerDocument, 'pointerdown', event => {
      if (!this.#form.contains(event.target)) this.#close();
    });
    listen(this.ownerDocument, 'pushe:layer:open', event => {
      if (event.detail?.source !== 'search') this.#close();
    });
    listen(this.ownerDocument, 'pushe:dialog:open', () => this.#close());
  }

  disconnectedCallback() {
    this.#off.splice(0).forEach(off => off());
    this.#close();
  }

  #panel() {
    let panel = this.#form.querySelector('.pushe-search__results');
    if (!panel) {
      const source = this.querySelector('#pushe-search-results-template')?.content.firstElementChild;
      if (!source) return null;
      panel = source.cloneNode(true);
      this.#form.append(panel);
    }
    return panel;
  }

  #cancel() {
    this.#revision++;
    this.#schedule.cancel();
    this.#request?.abort();
    this.#request = null;
  }

  #close() {
    this.#cancel();
    this.#form?.querySelector('.pushe-search__results')?.replaceChildren();
  }

  #changed() {
    this.#cancel();
    const query = this.#input.value.trim();
    const clear = this.#form.querySelector('.pushe-search__clear');
    if (query && !clear) {
      const source = this.querySelector('#pushe-search-clear-template')?.content.firstElementChild;
      if (source) this.#input.after(source.cloneNode(true));
    } else if (!query) clear?.remove();
    this.#panel()?.replaceChildren();
    if (shouldQuery(query)) this.#schedule(query);
  }

  async #query(query) {
    const revision = this.#revision;
    const request = new AbortController();
    this.#request = request;
    // The shared helper appends section_id, preserving the exact public URL contract without a duplicate key.
    const url = buildSuggestUrl(routesRoot(), query).replace(/&section_id=[^&]+$/, '');
    let timedOut = false;
    let onAbort;
    const cancelled = new Promise((_, reject) => {
      onAbort = () => reject(new DOMException('Aborted', 'AbortError'));
      request.signal.addEventListener('abort', onAbort, { once: true });
    });
    const timer = setTimeout(() => { timedOut = true; request.abort(); }, REQUEST_TIMEOUT_MS);
    try {
      const doc = await Promise.race([
        fetchSectionDocument('pushe-predictive-search', url, { signal: request.signal }),
        cancelled,
      ]);
      if (request.signal.aborted || revision !== this.#revision || !this.isConnected) return;
      const source = doc.querySelector('.pushe-search__results');
      const panel = this.#panel();
      if (!source || !panel) throw new Error('predictive_search_panel_missing');
      panel.replaceChildren(...Array.from(source.childNodes));
      if (panel.childNodes.length) emit('pushe:layer:open', { source: 'search' });
    } catch (error) {
      if (revision !== this.#revision || !this.isConnected) return;
      if (!timedOut && (request.signal.aborted || error.name === 'AbortError')) return;
      this.#panel()?.replaceChildren();
      showToast({ message: this.dataset.error, tone: 'error' });
    } finally {
      clearTimeout(timer);
      request.signal.removeEventListener('abort', onAbort);
      if (this.#request === request) this.#request = null;
    }
  }

  #key(event) {
    if (event.defaultPrevented) return;
    const panel = this.#form.querySelector('.pushe-search__results');
    if (event.key === 'Escape' && (panel?.childNodes.length || this.#request || this.#schedule.pending())) {
      event.preventDefault();
      // Focus first: a native focusin must not schedule a new query after Escape.
      this.#input.focus();
      this.#close();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
    const rows = Array.from(panel?.querySelectorAll('a.pushe-search__result') ?? []);
    if (!rows.length) return;
    const index = rows.indexOf(event.target);
    if (index < 0 && event.target !== this.#input) return;
    event.preventDefault();
    const next = event.key === 'ArrowDown' ? Math.min(index + 1, rows.length - 1) : index - 1;
    (next < 0 ? this.#input : rows[next]).focus();
  }
}

if (typeof customElements !== 'undefined') defineOnce('pushe-search', PushePredictiveSearch);
